<template>
  <div class="settlement-page">
    <PageHeader 
      title="정산 관리" 
      description="주문별 정산 데이터를 조회하고 관리합니다." 
      :margin-bottom="true"
    />

    <div class="content-container">
      <!-- 집계 정보 카드 -->
      <div class="aggregate-cards" v-if="aggregates">
        <div class="aggregate-card">
          <div class="card-label">전체 정산예정금액</div>
          <div class="card-value primary">{{ formatCurrency(aggregates.total_expected_settlement) }}원</div>
        </div>
        <div class="aggregate-card">
          <div class="card-label">출고확정 정산예정금액</div>
          <div class="card-value warning">{{ formatCurrency(aggregates.total_expected_settlement_shipped) }}원</div>
        </div>
        <div class="aggregate-card">
          <div class="card-label">전체 마진</div>
          <div class="card-value success">{{ formatCurrency(aggregates.total_margin) }}원</div>
        </div>
        <div class="aggregate-card">
          <div class="card-label">평균 마진율</div>
          <div class="card-value info">{{ formatPercent(aggregates.avg_margin_rate) }}%</div>
        </div>
      </div>

      <!-- 필터 섹션 -->
      <div class="filter-section">
        <el-input
          v-model="filters.productName"
          placeholder="상품명 검색"
          clearable
          style="width: 300px"
          @change="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>

        <el-select
          v-model="filters.recentDays"
          placeholder="기간 선택"
          style="width: 150px"
          @change="handleSearch"
        >
          <el-option label="이번달" :value="'thisMonth'" />
          <el-option label="최근 7일" :value="7" />
          <el-option label="최근 15일" :value="15" />
          <el-option label="최근 30일" :value="30" />
          <el-option label="최근 60일" :value="60" />
          <el-option label="최근 90일" :value="90" />
          <el-option label="최근 180일" :value="180" />
          <el-option label="최근 365일" :value="365" />
        </el-select>

        <el-button @click="handleSearch" type="primary">조회</el-button>
        
        <div class="spacer"></div>
        
        <el-button @click="openDailyStatsDialog">
          일별 통계
        </el-button>
        <el-button type="success" @click="openLedgerDialog">
          장부출력
        </el-button>
      </div>

      <!-- 테이블 섹션 -->
      <div class="table-section">
        <AppLoading v-if="loading" text="정산 데이터를 불러오는 중..." />
        
        <el-table
          v-else
          :data="tableData"
          stripe
          border
          style="width: 100%; height: 100%"
        >
          <el-table-column prop="order_number" label="주문번호" width="150" fixed />
          <el-table-column prop="product_name" label="상품명" min-width="200" show-overflow-tooltip />
          <el-table-column prop="status" label="출고상태" width="90" align="center">
            <template #default="{ row }">
              <el-tag :type="row.status === '출고완료' ? 'success' : 'info'" size="small">
                {{ row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="expected_settlement" label="정산예정금액" width="105" align="right">
            <template #default="{ row }">
              {{ formatCurrency(row.expected_settlement) }}
            </template>
          </el-table-column>
          <el-table-column prop="purchase_amount_krw" label="구매금액(KRW)" width="120" align="right">
            <template #default="{ row }">
              {{ formatCurrency(row.purchase_amount_krw) }}
            </template>
          </el-table-column>
          <el-table-column prop="actual_delivery_fee" label="배송비" width="100" align="right">
            <template #default="{ row }">
              {{ formatCurrency(row.actual_delivery_fee) }}
            </template>
          </el-table-column>
          <el-table-column prop="margin" label="마진" width="80" align="right">
            <template #default="{ row }">
              <span v-if="row.margin === null" class="null-value">-</span>
              <span v-else :class="{ 'negative-value': row.margin < 0, 'positive-value': row.margin > 0 }">
                {{ formatCurrency(row.margin) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="actual_margin_rate" label="마진율" width="95" align="right">
            <template #default="{ row }">
              <span v-if="row.actual_margin_rate === null" class="null-value">-</span>
              <span v-else :class="{ 'negative-value': row.actual_margin_rate < 0, 'positive-value': row.actual_margin_rate > 0 }">
                {{ formatPercent(row.actual_margin_rate) }}%
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="created_at" label="생성일" width="110" align="center">
            <template #default="{ row }">
              {{ formatDate(row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column label="작업" width="160" fixed="right" align="center">
            <template #default="{ row }">
              <div class="action-buttons">
                <el-button 
                  size="small" 
                  @click="openEditDialog(row)"
                >
                  수정
                </el-button>
                <el-button 
                  size="small" 
                  
                  @click="handleDelete(row.order_number)"
                  :loading="deletingOrders.has(row.order_number)"
                >
                  삭제
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 페이지네이션 -->
      <div class="pagination-section">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100, 500, 1000]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>

      <el-dialog
        v-model="editDialog.visible"
        title="정산 데이터 수정"
        width="480px"
        :close-on-click-modal="!editDialog.loading"
        :close-on-press-escape="!editDialog.loading"
        :show-close="!editDialog.loading"
        @closed="handleEditDialogClosed"
      >
        <el-form label-position="top">
          <el-form-item label="정산예정금액 (원)">
            <el-input
              v-model="editDialog.form.expectedSettlement"
              type="number"
              placeholder="예: 55000"
              clearable
            />
          </el-form-item>
          <el-form-item label="구매금액 (CNY)">
            <el-input
              v-model="editDialog.form.purchaseAmountCny"
              type="number"
              placeholder="예: 120.5"
              clearable
            />
          </el-form-item>
          <el-form-item label="실제 배송비 (원)">
            <el-input
              v-model="editDialog.form.actualDeliveryFee"
              type="number"
              placeholder="예: 15000"
              clearable
            />
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="handleEditDialogCancel" :disabled="editDialog.loading">취소</el-button>
            <el-button type="primary" @click="handleEditSubmit" :loading="editDialog.loading">저장</el-button>
          </span>
        </template>
      </el-dialog>

      <el-dialog
        v-model="ledgerDialog.visible"
        title="장부출력"
        width="420px"
        :close-on-click-modal="!ledgerDialog.loading"
        :close-on-press-escape="!ledgerDialog.loading"
        :show-close="!ledgerDialog.loading"
      >
        <el-form label-position="top">
          <el-form-item label="기간 선택">
            <el-date-picker
              v-model="ledgerDialog.dateRange"
              type="daterange"
              range-separator="~"
              start-placeholder="시작일"
              end-placeholder="종료일"
              value-format="YYYYMMDD"
              format="YYYY-MM-DD"
            />
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="ledgerDialog.visible = false" :disabled="ledgerDialog.loading">취소</el-button>
            <el-button type="primary" @click="handleLedgerExport" :loading="ledgerDialog.loading">다운로드</el-button>
          </span>
        </template>
      </el-dialog>

      <el-dialog
        v-model="dailyStatsDialog.visible"
        title="일별 정산 통계"
        width="900px"
      >
        <div class="daily-stats-dialog">
          <div class="daily-stats-toolbar">
            <div class="daily-stats-toolbar-left">
              <span class="daily-stats-label">조회 기간</span>
              <el-select
                v-model="dailyStatsDialog.days"
                style="width: 120px"
                @change="handleDailyStatsDaysChange"
              >
                <el-option label="7일" :value="7" />
                <el-option label="15일" :value="15" />
                <el-option label="30일" :value="30" />
                <el-option label="60일" :value="60" />
                <el-option label="90일" :value="90" />
                <el-option label="180일" :value="180" />
                <el-option label="365일" :value="365" />
              </el-select>
            </div>
            <el-button :loading="dailyStatsDialog.loading" @click="loadDailyStats">
              새로고침
            </el-button>
          </div>

          <div class="daily-stats-summary" v-if="hasDailyStats">
            <div class="aggregate-card">
              <div class="card-label">총 주문건수</div>
              <div class="card-value success">{{ totalDailyOrderCount.toLocaleString('ko-KR') }}건</div>
            </div>
            <div class="aggregate-card">
              <div class="card-label">총 정산예정금액</div>
              <div class="card-value primary">{{ formatCurrency(totalDailySettlement) }}원</div>
            </div>
            <div class="aggregate-card">
              <div class="card-label">일평균 주문건수</div>
              <div class="card-value info">{{ averageDailyOrderCount.toLocaleString('ko-KR') }}건</div>
            </div>
          </div>

          <div class="daily-chart-container" v-loading="dailyStatsDialog.loading">
            <Bar
              v-if="!dailyStatsDialog.loading && hasDailyStats"
              :data="dailyStatsChartData"
              :options="dailyStatsChartOptions"
            />
            <el-empty
              v-else-if="!dailyStatsDialog.loading"
              description="일별 정산 통계 데이터가 없습니다"
            />
          </div>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { computed, ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search } from '@element-plus/icons-vue';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import AppLoading from '@/components/common/loading.vue';
import PageHeader from '@/components/common/header.vue';
import {
  fetchSettlementData,
  fetchSettlementDailyStats,
  deleteSettlementData,
  patchSettlementData,
  exportLedger
} from '@/services/order';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default {
  name: 'SettlementPage',
  components: { AppLoading, PageHeader, Search, Bar },
  setup() {
    const loading = ref(false);
    const tableData = ref([]);
    const aggregates = ref(null);
    const deletingOrders = ref(new Set());
    const editDialog = reactive({
      visible: false,
      orderNumber: '',
      userid: '',
      loading: false,
      form: {
        expectedSettlement: '',
        purchaseAmountCny: '',
        actualDeliveryFee: ''
      }
    });
    const ledgerDialog = reactive({
      visible: false,
      loading: false,
      dateRange: []
    });
    const dailyStatsDialog = reactive({
      visible: false,
      loading: false,
      days: 30,
      data: []
    });

    const filters = reactive({
      productName: '',
      recentDays: 30
    });

    const getThisMonthDays = () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const msPerDay = 24 * 60 * 60 * 1000;
      return Math.floor((now - startOfMonth) / msPerDay) + 1;
    };

    const pagination = reactive({
      page: 1,
      pageSize: 20,
      total: 0
    });

    const formatCurrency = (value) => {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (Number.isNaN(num)) return '-';
      return num.toLocaleString('ko-KR');
    };

    const formatPercent = (value) => {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (Number.isNaN(num)) return '-';
      return num.toFixed(2);
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('ko-KR');
      } catch (e) {
        return '-';
      }
    };

    const formatChartDate = (dateStr) => {
      if (!dateStr) return '';
      const [year, month, day] = String(dateStr).split('-');
      if (!year || !month || !day) return dateStr;
      return `${month}/${day}`;
    };

    const hasDailyStats = computed(() => dailyStatsDialog.data.length > 0);

    const totalDailyOrderCount = computed(() => {
      return dailyStatsDialog.data.reduce((sum, item) => sum + (Number(item.orderCount) || 0), 0);
    });

    const totalDailySettlement = computed(() => {
      return dailyStatsDialog.data.reduce((sum, item) => sum + (Number(item.expectedSettlementSum) || 0), 0);
    });

    const averageDailyOrderCount = computed(() => {
      if (dailyStatsDialog.data.length === 0) return 0;
      return Math.round(totalDailyOrderCount.value / dailyStatsDialog.data.length);
    });

    const dailyStatsChartData = computed(() => {
      const rows = dailyStatsDialog.data;
      return {
        labels: rows.map((item) => formatChartDate(item.date)),
        datasets: [
          {
            type: 'bar',
            label: '정산예정금액',
            data: rows.map((item) => Number(item.expectedSettlementSum) || 0),
            yAxisID: 'ySettlement',
            backgroundColor: 'rgba(64, 158, 255, 0.32)',
            borderColor: '#409EFF',
            borderWidth: 1,
            borderRadius: 6,
            maxBarThickness: 28
          },
          {
            type: 'line',
            label: '주문건수',
            data: rows.map((item) => Number(item.orderCount) || 0),
            yAxisID: 'yCount',
            borderColor: '#67C23A',
            backgroundColor: '#67C23A',
            pointBackgroundColor: '#67C23A',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 5,
            tension: 0.3
          }
        ]
      };
    });

    const dailyStatsChartOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          position: 'top'
        },
        tooltip: {
          callbacks: {
            label(context) {
              const value = Number(context.raw) || 0;
              if (context.dataset.yAxisID === 'ySettlement') {
                return `${context.dataset.label}: ${value.toLocaleString('ko-KR')}원`;
              }
              return `${context.dataset.label}: ${value.toLocaleString('ko-KR')}건`;
            }
          }
        }
      },
      scales: {
        ySettlement: {
          type: 'linear',
          position: 'left',
          beginAtZero: true,
          ticks: {
            callback(value) {
              return `${Number(value).toLocaleString('ko-KR')}원`;
            }
          }
        },
        yCount: {
          type: 'linear',
          position: 'right',
          beginAtZero: true,
          grid: {
            drawOnChartArea: false
          },
          ticks: {
            callback(value) {
              return `${Number(value).toLocaleString('ko-KR')}건`;
            }
          }
        }
      }
    }));

    const loadSettlementData = async () => {
      loading.value = true;
      try {
        const recentDays =
          filters.recentDays === 'thisMonth' ? getThisMonthDays() : Number(filters.recentDays);

        const params = {
          page: pagination.page,
          pageSize: pagination.pageSize,
          recentDays
        };
        
        if (filters.productName && filters.productName.trim()) {
          params.productName = filters.productName.trim();
        }

        const res = await fetchSettlementData(params);
        
        if (res?.success) {
          tableData.value = res.data || [];
          pagination.total = res.total || 0;
          aggregates.value = res.aggregates || null;
        } else {
          ElMessage.error(res?.message || '정산 데이터 조회에 실패했습니다.');
          tableData.value = [];
          pagination.total = 0;
          aggregates.value = null;
        }
      } catch (error) {
        ElMessage.error(error?.response?.data?.message || '정산 데이터 조회 중 오류가 발생했습니다.');
        tableData.value = [];
        pagination.total = 0;
        aggregates.value = null;
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      pagination.page = 1;
      loadSettlementData();
    };

    const handlePageChange = (page) => {
      pagination.page = page;
      loadSettlementData();
    };

    const handleSizeChange = (size) => {
      pagination.pageSize = size;
      pagination.page = 1;
      loadSettlementData();
    };

    const handleDelete = async (orderNumber) => {
      try {
        await ElMessageBox.confirm(
          `주문번호 ${orderNumber}의 정산 데이터를 삭제하시겠습니까?`,
          '삭제 확인',
          {
            confirmButtonText: '삭제',
            cancelButtonText: '취소',
            type: 'warning'
          }
        );

        if (deletingOrders.value.has(orderNumber)) return;
        
        deletingOrders.value.add(orderNumber);
        try {
          const res = await deleteSettlementData(orderNumber);
          
          if (res?.success) {
            ElMessage.success(res?.message || '정산 데이터가 삭제되었습니다.');
            await loadSettlementData();
          } else {
            ElMessage.error(res?.message || '정산 데이터 삭제에 실패했습니다.');
          }
        } catch (error) {
          ElMessage.error(error?.response?.data?.message || '정산 데이터 삭제 중 오류가 발생했습니다.');
        } finally {
          deletingOrders.value.delete(orderNumber);
        }
      } catch (e) {
        // 취소한 경우
      }
    };

    const resetEditDialogForm = () => {
      editDialog.orderNumber = '';
      editDialog.userid = '';
      editDialog.form.expectedSettlement = '';
      editDialog.form.purchaseAmountCny = '';
      editDialog.form.actualDeliveryFee = '';
    };

    const openEditDialog = (row) => {
      editDialog.orderNumber = row.order_number;
      editDialog.userid = row?.userid || row?.user_id || row?.userId || '';
      editDialog.form.expectedSettlement = row?.expected_settlement ?? '';
      editDialog.form.purchaseAmountCny = row?.purchase_amount_cny ?? '';
      editDialog.form.actualDeliveryFee = row?.actual_delivery_fee ?? '';
      editDialog.visible = true;
    };

    const handleEditDialogCancel = () => {
      if (editDialog.loading) return;
      editDialog.visible = false;
    };

    const handleEditDialogClosed = () => {
      resetEditDialogForm();
      editDialog.loading = false;
    };

    const parseNumericInput = (value) => {
      if (value === '' || value === null || value === undefined) return null;
      const normalized = String(value).replace(/,/g, '');
      const num = Number(normalized);
      return Number.isNaN(num) ? NaN : num;
    };

    const toYYYYMMDD = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    };

    const setDefaultLedgerRange = () => {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      ledgerDialog.dateRange = [toYYYYMMDD(firstDay), toYYYYMMDD(today)];
    };

    const openLedgerDialog = () => {
      if (!Array.isArray(ledgerDialog.dateRange) || ledgerDialog.dateRange.length !== 2) {
        setDefaultLedgerRange();
      }
      ledgerDialog.visible = true;
    };

    const resolveStatsDays = () => {
      const rawDays = filters.recentDays === 'thisMonth'
        ? getThisMonthDays()
        : Number(filters.recentDays);

      if (!Number.isInteger(rawDays) || rawDays < 1) return 30;
      return Math.min(rawDays, 365);
    };

    const loadDailyStats = async () => {
      dailyStatsDialog.loading = true;
      try {
        const res = await fetchSettlementDailyStats({ days: dailyStatsDialog.days });
        if (res?.success) {
          dailyStatsDialog.data = Array.isArray(res.dailyStats) ? res.dailyStats : [];
        } else {
          dailyStatsDialog.data = [];
          ElMessage.error(res?.message || '일별 정산 통계 조회에 실패했습니다.');
        }
      } catch (error) {
        dailyStatsDialog.data = [];
        ElMessage.error(error?.response?.data?.message || '일별 정산 통계 조회 중 오류가 발생했습니다.');
      } finally {
        dailyStatsDialog.loading = false;
      }
    };

    const openDailyStatsDialog = async () => {
      dailyStatsDialog.days = resolveStatsDays();
      dailyStatsDialog.visible = true;
      await loadDailyStats();
    };

    const handleDailyStatsDaysChange = async () => {
      await loadDailyStats();
    };

    const getFileNameFromDisposition = (disposition) => {
      const raw = String(disposition || '');
      if (!raw) return '';
      const utf8Match = raw.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8Match && utf8Match[1]) return decodeURIComponent(utf8Match[1]);
      const normalMatch = raw.match(/filename="?([^"]+)"?/i);
      if (normalMatch && normalMatch[1]) return normalMatch[1];
      return '';
    };

    const parseBlobErrorMessage = async (blobData) => {
      if (!(blobData instanceof Blob)) return '';
      try {
        const text = await blobData.text();
        if (!text) return '';
        const parsed = JSON.parse(text);
        return parsed?.message || '';
      } catch (e) {
        return '';
      }
    };

    const handleLedgerExport = async () => {
      if (!Array.isArray(ledgerDialog.dateRange) || ledgerDialog.dateRange.length !== 2) {
        ElMessage.warning('출력 기간을 선택하세요.');
        return;
      }
      const [startDate, endDate] = ledgerDialog.dateRange;
      if (!startDate || !endDate) {
        ElMessage.warning('출력 기간을 선택하세요.');
        return;
      }

      ledgerDialog.loading = true;
      try {
        const response = await exportLedger({ startDate, endDate });
        const blob = response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
        const fileName = getFileNameFromDisposition(response?.headers?.['content-disposition']) || `ledger_${startDate}_${endDate}.xlsx`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        ElMessage.success('장부 파일 다운로드를 시작했습니다.');
        ledgerDialog.visible = false;
      } catch (error) {
        const apiMessage = await parseBlobErrorMessage(error?.response?.data);
        ElMessage.error(apiMessage || error?.response?.data?.message || '장부 출력에 실패했습니다.');
      } finally {
        ledgerDialog.loading = false;
      }
    };

    const handleEditSubmit = async () => {
      if (!editDialog.orderNumber) return;

      const payload = {};

      const expected = parseNumericInput(editDialog.form.expectedSettlement);
      if (expected !== null) {
        if (Number.isNaN(expected)) {
          ElMessage.warning('정산예정금액을 숫자로 입력하세요.');
          return;
        }
        payload.expectedSettlement = expected;
      }

      const purchaseAmount = parseNumericInput(editDialog.form.purchaseAmountCny);
      if (purchaseAmount !== null) {
        if (Number.isNaN(purchaseAmount)) {
          ElMessage.warning('구매금액(CNY)을 숫자로 입력하세요.');
          return;
        }
        payload.purchaseAmountCny = purchaseAmount;
      }

      const actualFee = parseNumericInput(editDialog.form.actualDeliveryFee);
      if (actualFee !== null) {
        if (Number.isNaN(actualFee)) {
          ElMessage.warning('실제 배송비를 숫자로 입력하세요.');
          return;
        }
        payload.actualDeliveryFee = actualFee;
      }

      if (Object.keys(payload).length === 0) {
        ElMessage.warning('최소 하나 이상의 값을 입력하세요.');
        return;
      }

      if (editDialog.userid) {
        payload.userid = editDialog.userid;
      }

      editDialog.loading = true;
      try {
        const res = await patchSettlementData(editDialog.orderNumber, payload);
        if (res?.success) {
          ElMessage.success(res?.message || '정산 데이터가 수정되었습니다.');
          editDialog.visible = false;
          await loadSettlementData();
        } else {
          ElMessage.error(res?.message || '정산 데이터 수정에 실패했습니다.');
        }
      } catch (error) {
        ElMessage.error(error?.response?.data?.message || '정산 데이터 수정 중 오류가 발생했습니다.');
      } finally {
        editDialog.loading = false;
      }
    };

    onMounted(async () => {
      await loadSettlementData();
    });

    return {
      loading,
      tableData,
      aggregates,
      filters,
      pagination,
      deletingOrders,
      editDialog,
      ledgerDialog,
      dailyStatsDialog,
      hasDailyStats,
      totalDailyOrderCount,
      totalDailySettlement,
      averageDailyOrderCount,
      dailyStatsChartData,
      dailyStatsChartOptions,
      formatCurrency,
      formatPercent,
      formatDate,
      handleSearch,
      handlePageChange,
      handleSizeChange,
      handleDelete,
      openEditDialog,
      handleEditDialogCancel,
      handleEditDialogClosed,
      handleEditSubmit,
      loadDailyStats,
      openDailyStatsDialog,
      handleDailyStatsDaysChange,
      openLedgerDialog,
      handleLedgerExport
    };
  }
};
</script>

<style scoped>
.settlement-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--el-bg-color-page);
}

.content-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.aggregate-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--spacing-xs);
}

.aggregate-card {
  background: var(--el-bg-color);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  padding: var(--spacing-sm);
  text-align: center;
}

.card-label {
  font-size: 14px;
  color: var(--el-text-color-secondary);
}

.card-value {
  font-weight: bold;
  font-size: 18px;
}

.card-value.primary {
  color: var(--el-color-primary);
}

.card-value.success {
  color: var(--el-color-success);
}

.card-value.info {
  color: var(--el-color-info);
}

.card-value.warning {
  color: var(--el-color-warning);
}

.filter-section {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
  background: var(--el-bg-color);
  padding: 6px;
  border-radius: 8px;
  border: 1px solid var(--el-border-color-lighter);
  flex-wrap: wrap;
}

.spacer {
  flex: 1;
}

.table-section {
  flex: 1;
  overflow: hidden;
  background: var(--el-bg-color);
  border-radius: 8px;
  border: 1px solid var(--el-border-color-lighter);
  padding: var(--spacing-md);
}

.pagination-section {
  display: flex;
  justify-content: center;
}

.null-value {
  color: var(--el-text-color-placeholder);
}

.negative-value {
  color: var(--el-color-danger);
  font-weight: bold;
}

.positive-value {
  color: var(--el-color-success);
  font-weight: bold;
}

.action-buttons {
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
}


.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.daily-stats-dialog {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.daily-stats-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.daily-stats-toolbar-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.daily-stats-label {
  color: var(--el-text-color-secondary);
  font-weight: var(--el-font-weight-medium);
}

.daily-stats-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-xs);
}

.daily-chart-container {
  height: 380px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  padding: var(--spacing-md);
  background: var(--el-bg-color);
}

@media (max-width: 768px) {
  .aggregate-cards {
    grid-template-columns: 1fr;
  }

  .filter-section {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-section > * {
    width: 100% !important;
  }

  .spacer {
    display: none;
  }

  .daily-stats-summary {
    grid-template-columns: 1fr;
  }

  .daily-chart-container {
    height: 300px;
  }
}
</style>

